<template>
  <div>
    <full-calendar
      ref="calendar"
      :options="calendarOptions"
      class="calendar-mobile"
    >
      <template v-slot:eventContent="arg">
        <event-card :arg="arg" />
      </template>
    </full-calendar>

    <event-detail-modal
      ref="eventDetailModalRef"
      :selectedEvent="selectedEvent"
      @start-delete="openEventDeletionModal"
      @start-delete-vcr="openEventDeletionVCRModal"
      @start-modify="openEventModificationModal"
      @start-cancel="openEventCancellationModal"
    />

    <event-modification-modal
      ref="eventModificationModalRef"
      :selectedEvent="selectedEvent"
    />

    <event-deletion-modal
      ref="eventDeletionModalRef"
      :selectedEvent="selectedEvent"
    />
    <event-deletion-v-c-r-modal
      ref="eventDeletionVCRModalRef"
      :selectedEvent="selectedEvent"
    />
    <EventCancellationModal
      ref="eventCancellationModalRef"
      :selectedEvent="selectedEvent"
      @close-detail-modal="closeEventDetailModal"
    />
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import viLocale from "@fullcalendar/core/locales/vi";
import enGB from "@fullcalendar/core/locales/en-gb";
import EventCard from "./Event/Card.vue";
import EventDetailModal from "./Event/Modals/Detail.vue";
import EventDeletionModal from "./Event/Modals/Deletion.vue";
import EventDeletionVCRModal from "./Event/Modals/VCRDeletion.vue";
import EventModificationModal from "./Event/Modals/Modification.vue";
import EventCancellationModal from "./Event/Modals/Cancellation.vue";
import { EventAPI } from "./Event/event-utils";

import { store } from "@/store";
import { getId, Message } from "@/helpers/utils";
import { getDate } from "@/helpers/datetime-helper";
import Role from "@/constants/Role";
import { hasValidRole } from "@/helpers/role-helper";
import { mapGetters, mapState } from "vuex";
import moment from "moment";
const generateHeaderToolBarCfg = role => {
  if (
    hasValidRole(role, [Role.Admin, Role.Principal]) ||
    !hasValidRole(role, Object.values(Role))
  ) {
    return "dayGridMonth,timeGridWeek,timeGridDay";
  }

  return "dayGridMonth,timeGridWeek";
};

export default {
  components: {
    FullCalendar,
    EventCard,
    EventDetailModal,
    EventDeletionModal,
    EventDeletionVCRModal,
    EventModificationModal,
    EventCancellationModal
  },
  inject: ["getEvents"],
  props: ["initialDate"],
  data() {
    const timeZone = store.getters["user/timezone"];
    const userRole = store.getters["user/role"];

    return {
      isMounted: false,
      calendarOptions: {
        timeZone: timeZone ?? "local",
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
          momentTimezonePlugin
        ],
        headerToolbar: {
          left: "title",
          center: generateHeaderToolBarCfg(userRole),
          right: "prev,next today"
        },
        initialDate: this.initialDate || moment().format("YYYY-MM"),
        initialView: "dayGridMonth",
        events: this.getEvents,
        editable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: 2,
        weekends: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents,
        displayEventEnd: true,
        eventTimeFormat: {
          hour: "2-digit",
          minute: "2-digit"
        },
        eventDurationEditable: false, // disable resizing
        eventChange: this.handleEventChange,
        eventDidMount: this.eventDidMount,
        locale: "",
        contentHeight: 1000
        /* you can update a remote database when these fire:
        eventAdd:
        eventRemove:
        */
      },
      currentEvents: [],
      selectedEvent: {},
      calendarApi: null
    };
  },
  computed: {
    ...mapGetters("user", {
      userTz: "timezone"
    }),
    ...mapState("switchLanguage", ["localeConfig"])
  },
  methods: {
    handleWeekendsToggle() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends; // update a property
    },

    handleEventClick(clickInfo) {
      this.selectedEvent = clickInfo;
      this.$refs.eventDetailModalRef.open();
    },

    handleEvents(events) {
      this.currentEvents = events;
    },

    openEventDeletionModal() {
      this.$refs.eventDeletionModalRef.open();
    },
    openEventDeletionVCRModal(vcr) {
      this.$refs.eventDeletionVCRModalRef.open(vcr);
    },
    closeEventDetailModal() {
      this.$refs.eventDetailModalRef.close();
    },

    openEventModificationModal() {
      this.$refs.eventModificationModalRef.open();
    },
    openEventCancellationModal() {
      this.$refs.eventCancellationModalRef.open();
    },

    getCalendarApi() {
      return this.calendarApi;
    },

    handleEventChange(evEvent) {
      const eventInfo = evEvent.event || {};
      const eventId = getId(eventInfo.extendedProps);

      // dropped-event always has start & end in a same day;
      const newDate = getDate(eventInfo.startStr);
      const eventType = eventInfo.extendedProps?.type;

      EventAPI.edit(
        eventId,
        {
          eventType,
          eventConfig: { start: newDate, end: newDate }
          // overlayLoading: false
        },
        true
      ).then(() => {
        Message.success("Update event successfully!");
      });
    },

    // bcz event background-color config not work, replace by using background property
    eventDidMount(info) {
      if (info.event.backgroundColor) {
        info.el.style.background = info.event.backgroundColor;
      }
    },

    setLocaleCalendar() {
      this.calendarOptions.locale =
        this.localeConfig === "vi" ? viLocale : enGB;
    }
  },
  mounted() {
    this.calendarApi = this.$refs.calendar.getApi();
    setTimeout(() => {
      if (this.calendarApi) {
        this.calendarApi.updateSize();
        this.calendarApi.render();
      }
    }, 200);

    this.setLocaleCalendar();
  },
  watch: {
    userTz: function(newUserTz) {
      this.calendarApi.setOption("timeZone", newUserTz);
    },
    localeConfig() {
      this.setLocaleCalendar();
    }
  }
};
</script>

<style lang="scss">
@media screen and (max-width: 599px) {
  .fc table {
    font-size: 10px !important;
  }
  .calendar-mobile {
    & .fc-header-toolbar {
      padding-left: 0 !important;
      padding-right: 0 !important;
      flex-wrap: wrap !important;
    }

    & .fc-toolbar-chunk:first-child {
      min-width: 100%;
      max-width: 100%;
      flex: 1 1 100%;
    }

    .fc-toolbar-title {
      font-size: 20px !important;
    }

    .fc-button {
      font-size: 10px !important;
    }
  }
}
a.fc-event.fc-event-draggable.fc-event-draggable {
  min-height: 36px;
}
.fc-timegrid-slots table tbody tr .fc-timegrid-slot-label {
  height: 50px !important;
}
</style>
